import { UIIcon } from '../config/icons';
import { ABFilterBar } from '../lib/ab/filter-bar';
import { cEventActiveSessions, CEventAdminView, cEventsProducerView, CSession, cSessionSalesEnd, cSessionSalesEnded, cSessionUpdate } from '../lib/mdl/c-event-admin';
import { c_venues, CVenueSceneView } from '../lib/mdl/c-venue';
import { haveRoleInProducer } from '../lib/me/can-producer';
import { the_alt_token } from '../lib/me/login';
import { the_producer } from '../lib/me/producer';
import { uiSetBG } from '../lib/ui/bg';
import { UIButton } from '../lib/ui/component/button';
import { UIDialog } from '../lib/ui/component/dialog';
import { UINumberInput } from '../lib/ui/component/input';
import { UITable, UITableDefinition } from '../lib/ui/component/table';
import { uiToast } from '../lib/ui/component/toast';
import { addTooltip } from '../lib/ui/component/tooltip';
import { $el } from '../lib/ui/elements';
import { $fmt } from '../lib/ui/fmt';
import { UIScreen } from '../lib/ui/screen';
import { UIAlignment, UIRelPos } from '../lib/ui/types';
import { UIViewSpec } from '../lib/ui/view';
import { YArrayDataSource } from '../lib/y/data-source';
import { xavis_experiments } from '../lib/y/debug';
import { $on } from '../lib/y/events';
import { $fn } from '../lib/y/fn';
import { $form } from '../lib/y/form';
import { $i18n, yGetI18N, yInterpolate } from '../lib/y/i18n';
import { $location } from '../lib/y/location';
import { $date, $time, YDate } from '../lib/y/time';
import { YId } from '../lib/y/types';
import { ProducerEventBookingsScreen } from './event_bookings';

type EventSession = [CEventAdminView, CSession];

class AddTicketsDialog extends UIDialog {
    constructor(event: CEventAdminView, session: CSession) {
        super({ classes: 'm-mobile-fs' });

        const input = new UINumberInput({ kind: 'k-subtle', placeholder: $i18n.pr.add_tickets_placeholder, min: session.quantity_reserved, required: true });
        const increase = $el.DIV('t-c-soft tl-producer-under-input-text', '\u00a0');

        $on(input, 'value', () => {
            const value = input.value;
            if(value == null || value == session.quantity) {
                increase.textContent = '\u00a0';
            } else if(value > session.quantity) {
                increase.textContent = '+' + (value - session.quantity) + ' ' + $i18n.aa.o_tickets;
            } else if(value < session.quantity) {
                increase.textContent = '-' + (session.quantity - value) + ' ' + $i18n.aa.o_tickets;
            }
        })

        this.content.ui_add($el.DIV('w-block l-form m-compact', [
            $el.DIV('asset-tickets'),
            $el.H3('title', $i18n.pr.add_tickets_title),
            $el.DIV('tl-producer-event-box', [
                $el.DIV('r-title', yGetI18N(event.name)),
                $el.DIV('r-time', $time(session.time).format('EEE d MMM HH:mm') + (session.is_drop_in ? (' - ' + $time(session.end_time).format('HH:mm')) : '')),
                $el.B(session.quantity_booked + ' / ' + session.quantity + ' ' + $i18n.aa.c_booked + ' \u00b7 ' + session.quantity_standby + ' ' + $i18n.aa.c_standby),
            ]),
            $el.P('text', yInterpolate($i18n.pr.add_tickets_current_amount, '' + session.quantity)),
            input,
            increase,
            $el.DIV('s-medium'),
            $el.DIV('l-button-row', [
                new UIButton({ title: $i18n.tl.b_cancel, classes: 'm-hollow ui-tint-reverse', on_click: () => { this.dismiss(null) } }),
                new UIButton({
                    title: $i18n.pr.add_tickets_apply, classes: 'ui-tint-reverse', on_click: () => {
                        if(!$form.validate(input)) return;

                        const value = input.value;
                        if(value == null) return;

                        cSessionUpdate(event, session.id, { quantity: value }).then(() => {
                            session.quantity = value;
                            this.dismiss(true);
                        }, uiToast);
                    }
                })
            ])
        ]));
    }
}

export class ProducerBookingsScreen extends UIScreen {
    private _source!: YArrayDataSource<EventSession>;
    private _all_sessions!: EventSession[];
    private _filter_bar!: ABFilterBar;

    constructor() {
        super();
    }

    protected override async fillContent(content: UIViewSpec) {
        const start_of_day = $date.today().start;
        const sessions: EventSession[] = [];
        const all_events: CEventAdminView[] = [];
        const venue_set = new Set<YId>();
        const all_venues: CVenueSceneView[] = [];
        for(const e of await cEventsProducerView(the_producer!.id)) {
            if(!xavis_experiments && !e.is_published) continue;
            let has_session = false;
            const active_sessions = cEventActiveSessions(e);
            for(const s of active_sessions) {
                if($time(s.time).after(start_of_day)) {
                    has_session = true;
                    sessions.push([e, s]);
                }
            }

            if(has_session) {
                all_events.push(e);
                if(!venue_set.has(e.venue_id)) {
                    venue_set.add(e.venue_id);
                    const venue = c_venues.maybe(e.venue_id);
                    if(venue)
                        all_venues.push(venue);
                }
            }
        }
        this._all_sessions = sessions;

        const doSearch = () => this._doSearch();

        const filter_bar = this._filter_bar = new ABFilterBar({ on_search: doSearch, on_filter: doSearch });
        const filters = filter_bar.filters;

        filters.addSelect('event', $i18n.pr.filter_events, all_events.map(e => ({ key: e.id, name: yGetI18N(e.name) })).sort((a, b) => $fn.ICOMPARE(a.name, b.name)), $i18n.pr.filter_events_all);
        filters.addSelect('venue', $i18n.pr.filter_venue, all_venues.map(v => ({ key: v.id, name: v.name })).sort((a, b) => $fn.ICOMPARE(a.name, b.name)), $i18n.pr.filter_venues_all);
        // filters.addCustom('city', $i18n.pr.filter_city, all_cities.map(c => ({ key: c.id, name: c.name })).sort((a, b) => $fn.ICOMPARE(a.name, b.name)), $i18n.pr.filter_cities_all);
        filters.addDateRange('date', $i18n.pr.filter_time_period);

        const def = new UITableDefinition<EventSession>();
        def.row_classes = 'm-admin tl-admin-table-row'; // check if we really need the tl-admin-table-row
        def.row_header_classes = 'tl-producer-table-header';
        def.addCustom({
            width: -74, classes: 'tl-producer-thumb-cell', decorator: (td, row) => {
                const model = row.item[0];
                const image = $el.DIV('r-image');
                uiSetBG(image, { media: model.picture_ids?.[0], variant: 'size-500' });
                td.appendChild(image);
            }
        });

        def.addValue(i => yGetI18N(i[0].name), { title: $i18n.pr.sessions_col_event, width: 125 });
        def.addValue(i => $time(i[1].time).format('EEE d MMM HH:mm') + (i[1].is_drop_in ? (' - ' + $time(i[1].end_time).format('HH:mm')) : ''), { title: $i18n.pr.sessions_col_date_time, width: -160, sort: i => i[1].time });
        def.addValue(i => c_venues.maybe(i[0].venue_id)?.name ?? '', { title: $i18n.pr.sessions_col_venue, width: 100, wide_only: true });

        def.addCustom({
            title: $i18n.pr.sessions_col_ticket_type, width: -100, decorator: (td, row) => {
                const item = row.item;
                td.ui_add($el.SPAN('t-underline-dotted', item[1].ticket_type == 'pickup' ? $i18n.pr.ticket_guestlist : $i18n.pr.ticket_eticket));
                if(item[1].ticket_type == 'email') {
                    addTooltip(yInterpolate($i18n.pr.sessions_tooltip_eticket, cSessionSalesEnd(item[1]).format('d MMM HH:mm')), td, { position: UIRelPos.Below, offset: 5 });
                } else {
                    const bookings_emails = item[0].producer_emails.filter(e => e.kinds.includes('booking_list')).map(e => e.email);
                    addTooltip(yInterpolate($i18n.pr.sessions_tooltip_guestlist, cSessionSalesEnd(item[1]).format('d MMM HH:mm'), bookings_emails.join(', ') || ''), td, { position: UIRelPos.Below, offset: 5 });
                }

                // if(up + down > 0) {
                //     const span = $el.I(`${(up/(up+down)*100).toFixed(0)}%`);
                //     addTooltip(`${up} up / ${down} down`, row, { anchor: span, hover: td, position: UIRelPos.Above, offset: 5 });
                //     td.ui_add(span);
                // } else {
                //     td.ui_add('-');
                // }
            }, comparator: (a, b, o) => o * $fn.ICOMPARE(a[1].ticket_type, b[1].ticket_type)
        });

        def.addCustom({
            title: $i18n.pr.sessions_col_bookings, width: -85, decorator: (td, row) => {
                const session = row.item[1];
                const chip = $el.DIV('tl-producer-chip m-small m-push-left', $el.I(session.quantity_reserved + ' / ' + session.quantity));
                if(session.quantity_reserved != session.quantity)
                    chip.classList.add('m-transparent');

                if(session.quantity_booked != session.quantity_reserved) {
                    addTooltip(yInterpolate($i18n.aa.tt_session_booked, session.quantity_booked, session.quantity_reserved - session.quantity_booked), chip,
                        { position: UIRelPos.Above, offset: 5 });
                    chip.classList.add('tl-tooltip-underline');
                }

                td.appendChild(chip);
            }
        });
        def.addValue(i => i[1].quantity_standby, $fmt.NUMBER, { title: $i18n.pr.sessions_col_standby, width: -85, sort: false, alignment: UIAlignment.Left });
        def.addValue(i => cSessionSalesEnd(i[1]), $fmt.DATE('d MMM HH:mm'), { title: $i18n.pr.sessions_col_closes_at, width: -110 });
        // def.addValue(i => i[1].tickets_sent, $fmt.CHECKMARK, { title: 'Sent', width: -80 });

        def.addCustom({
            width: -180, decorator: (td, row) => {
                if(cSessionSalesEnded(row.item[1])) {
                    if(row.item[1].ticket_type == 'email') {
                        td.ui_add(new UIButton({
                            title: $i18n.pr.event_booking_list, classes: 'ui-tint-subtle m-compacter', on_click: () => {
                                $location.push({ transient: new ProducerEventBookingsScreen(row.item[0], row.item[1]) })
                            }
                        }));
                    } else {
                        td.ui_add(new UIButton({ title: $i18n.pr.event_booking_list, left_icon: UIIcon.ARROW_UP_RIGHT_FROM_SQUARE, classes: 'ui-tint-subtle m-compacter', link: `/booking_list_redirect/${row.item[1].id}?token=${the_alt_token}` }));
                    }
                } else {
                    if(haveRoleInProducer('events', the_producer!.id)) {
                        if(!row.item[1].managed_by_id) {
                            td.ui_add(new UIButton({
                                title: $i18n.pr.events_add_tickets, left_icon: UIIcon.PLUS, classes: 'ui-tint-subtle m-compacter', on_click: () => {
                                    new AddTicketsDialog(row.item[0], row.item[1]).present().then(() => {
                                        doSearch();
                                    });
                                }
                            }));
                        }
                    }
                }
            }
        });

        // def.addButtons([
        //     { icon: UIIcon.LIST_UL, tooltip: 'Booking list', action: item => {
        //         // $location.push('event_bookings', { event: item[0].model.id, session: item[1].id });
        //     }},
        // ], { width: -31 });
        def.preference_prefix = 'upcoming';

        // $tl.getUpcomingEvents().then(events => {
        //     const now = $time.now();
        //     const sessions: EventSession[] = [];
        //     for(const e of events) {
        //         if(!e.model.published) continue;
        //         for(const s of e.active_sessions) {
        //             if(s.time_y.after(now) && s.ticket == 'email') {
        //                 sessions.push([e,s]);
        //             }
        //         }
        //     }
        //     this._all_sessions = sessions;
        //     this._doSearch();

        //     $on(this.search_input!, 'value', () => { this._doSearch() });
        //     // $on(this.city_toggle!, 'value', () => { this._doSearch() }); // TODO
        //     $on(show_sent, 'value', () => { this._doSearch() });
        //     $on(include_integrations, 'value', () => { this._doSearch() });
        // });

        content.ui_add($el.DIV('ui-section', $el.DIV('tl-new-list-block m-wide', [
            $el.P('screen-title', $i18n.pr.sessions_title),
            $el.P('screen-mobile-title', $i18n.pr.sessions_mobile_title),
            filter_bar,
            new UITable(def, this._source = new YArrayDataSource, { table_element_classes: 'tl-producer-table-element', classes: 'tl-mobile-grid-table' })
        ])));

        doSearch();
    }

    private _doSearch() {
        // const matcher = this.filter_matcher;
        let sessions = this._all_sessions/*.filter(i => matcher(i[0]));*/
        // sessions = filterSessions(sessions, this.filters!.filters);
        // if(!this._show_sent.value)
        //     sessions = sessions.filter(i => !i[1].tickets_sent);
        const filter_value = this._filter_bar.filters.value;
        if(filter_value.event) {
            const event = filter_value.event;
            sessions = sessions.filter(s => s[0].id == event);
        }
        if(filter_value.venue) {
            const venue = filter_value.venue;
            sessions = sessions.filter(s => s[0].venue_id == venue);
        }
        if(filter_value.city) {
            const city = filter_value.city;
            sessions = sessions.filter(s => s[0].city_id == city);
        }
        if(filter_value.date) {
            const date: [YDate, YDate] = filter_value.date;
            sessions = sessions.filter(s => $time(s[1].time).after(date[0].start) && $time(s[1].time).before(date[1].end));
        }

        sessions = this._filter_bar.search_input!.filterArray(sessions, (s, w) => {
            if(yGetI18N(s[0].name).toLowerCase().includes(w)) return true;
            const venue = c_venues.maybe(s[0].venue_id);
            if(venue?.name.toLowerCase().includes(w)) return true;
            return false;
        });

        this._source.data = sessions;
    }

    override onViewDidReappear() {
        this._doSearch();
    }
}